import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';

interface DailyReport {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  department: string;
  reportDate: string;
  workContent: string;
  achievements: string;
  challenges: string;
  tomorrowPlan: string;
  workHours: number;
  overtime: number;
  isEmergency: boolean;
  emergencyContent: string;
  status: string;
  reviewedBy: string;
  reviewerName: string;
  reviewComment: string;
  reviewedAt: string;
  createdAt: string;
  updatedAt: string;
}

export default function DailyReportDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isManager = user?.role === 'admin' || user?.role === 'system_admin' || user?.role === 'manager';

  useEffect(() => {
    if (id) {
      fetchReport();
    }
  }, [id]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const data = await api.get<DailyReport>(`/api/daily-reports/${id}`);
      setReport(data);
    } catch (error) {
      console.error('Failed to fetch daily report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!report || submitting) return;
    setSubmitting(true);
    try {
      await api.put(`/api/daily-reports/${report.id}/review`, {
        status: 'reviewed',
        reviewComment,
      });
      setShowReviewModal(false);
      fetchReport();
    } catch (error) {
      console.error('Failed to approve report:', error);
      toast.error('承認に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!report || !reviewComment.trim()) {
      toast.error('却下理由を入力してください');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.put(`/api/daily-reports/${report.id}/review`, {
        status: 'rejected',
        reviewComment,
      });
      setShowReviewModal(false);
      fetchReport();
    } catch (error) {
      console.error('Failed to reject report:', error);
      toast.error('却下に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'success' | 'danger'> = {
      draft: 'default',
      submitted: 'warning',
      reviewed: 'success',
      rejected: 'danger',
    };
    const labels: Record<string, string> = {
      draft: '下書き',
      submitted: '提出済',
      reviewed: '承認済',
      rejected: '却下',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">日報が見つかりません</p>
        <Button variant="secondary" onClick={() => navigate('/daily-reports')} className="mt-4">
          一覧に戻る
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/daily-reports')}>
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            戻る
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            日報詳細
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(report.status)}
          {report.isEmergency && (
            <Badge variant="danger">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              緊急
            </Badge>
          )}
        </div>
      </div>

      {/* 基本情報 */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">報告日</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {new Date(report.reportDate).toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'short',
              })}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">報告者</p>
            <p className="font-medium text-gray-900 dark:text-white">{report.userName}</p>
            <p className="text-xs text-gray-500">{report.department}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">勤務時間</p>
            <p className="font-medium text-gray-900 dark:text-white">{report.workHours}時間</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">残業時間</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {report.overtime > 0 ? (
                <span className="text-orange-600">{report.overtime}時間</span>
              ) : (
                '0時間'
              )}
            </p>
          </div>
        </div>

        <hr className="border-gray-200 dark:border-gray-700 my-4" />

        {/* 緊急報告 */}
        {report.isEmergency && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="font-semibold text-red-800 dark:text-red-200">緊急報告</h3>
            </div>
            <p className="text-red-700 dark:text-red-300 whitespace-pre-wrap">{report.emergencyContent}</p>
          </div>
        )}

        {/* 業務内容 */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">業務内容</h3>
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{report.workContent}</p>
            </div>
          </div>

          {report.achievements && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">成果・進捗</h3>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{report.achievements}</p>
              </div>
            </div>
          )}

          {report.challenges && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">課題・問題点</h3>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{report.challenges}</p>
              </div>
            </div>
          )}

          {report.tomorrowPlan && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">明日の予定</h3>
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{report.tomorrowPlan}</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* レビュー情報 */}
      {(report.status === 'reviewed' || report.status === 'rejected') && report.reviewerName && (
        <Card>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            レビュー情報
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">レビュー者</p>
              <p className="font-medium text-gray-900 dark:text-white">{report.reviewerName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">レビュー日時</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {new Date(report.reviewedAt).toLocaleString('ja-JP')}
              </p>
            </div>
          </div>
          {report.reviewComment && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">コメント</p>
              <div className={`p-4 rounded-lg ${
                report.status === 'rejected' 
                  ? 'bg-red-50 dark:bg-red-900/20' 
                  : 'bg-gray-50 dark:bg-gray-800'
              }`}>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{report.reviewComment}</p>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* 管理者アクション */}
      {isManager && report.status === 'submitted' && (
        <Card>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">レビューアクション</h3>
              <p className="text-sm text-gray-500">この日報を確認して承認または却下してください</p>
            </div>
            <Button onClick={() => setShowReviewModal(true)}>
              レビューする
            </Button>
          </div>
        </Card>
      )}

      {/* メタ情報 */}
      <div className="text-sm text-gray-500 dark:text-gray-400 text-right">
        <p>作成日時: {new Date(report.createdAt).toLocaleString('ja-JP')}</p>
        {report.updatedAt !== report.createdAt && (
          <p>更新日時: {new Date(report.updatedAt).toLocaleString('ja-JP')}</p>
        )}
      </div>

      {/* レビューモーダル */}
      <Modal isOpen={showReviewModal} onClose={() => setShowReviewModal(false)} title="日報レビュー" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              レビューコメント
            </label>
            <textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={4}
              placeholder="コメントを入力（却下時は必須）"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowReviewModal(false)} disabled={submitting}>
              キャンセル
            </Button>
            <Button type="button" variant="danger" onClick={handleReject} disabled={submitting}>
              {submitting ? '処理中...' : '却下'}
            </Button>
            <Button type="button" onClick={handleApprove} disabled={submitting}>
              {submitting ? '処理中...' : '承認'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
